import React, { Component } from 'react';
import { Section } from './Section';
import { Trophy, Award, Star, Shield, Heart, MessageSquare, Image, Gift, Zap, ChevronRight } from 'lucide-react';
interface Badge {
  id: string;
  title: string;
  description: string;
  icon: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  unlocked: boolean;
}
interface Milestone {
  id: string;
  title: string;
  level: number;
  maxLevel: number;
  progress: number;
  total: number;
  category: 'lover' | 'messenger' | 'collector' | 'supporter';
}
export const AchievementsMini: React.FC = () => {
  const badges: Badge[] = [{
    id: 'badge-1',
    title: 'First Connection',
    description: 'Made your first connection with an angel',
    icon: 'Heart',
    rarity: 'common',
    unlocked: true
  }, {
    id: 'badge-2',
    title: 'Ritual Master',
    description: 'Completed all daily rituals for 7 days straight',
    icon: 'Trophy',
    rarity: 'rare',
    unlocked: true
  }, {
    id: 'badge-3',
    title: 'Generous Tipper',
    description: 'Sent over 1,000 tokens in tips',
    icon: 'Gift',
    rarity: 'epic',
    unlocked: true
  }, {
    id: 'badge-4',
    title: 'VIP Patron',
    description: 'Maintained VIP status for 30 days',
    icon: 'Star',
    rarity: 'rare',
    unlocked: true
  }, {
    id: 'badge-5',
    title: 'Legendary Collector',
    description: 'Collected all legendary angel skins',
    icon: 'Award',
    rarity: 'legendary',
    unlocked: false
  }, {
    id: 'badge-6',
    title: 'Chat Champion',
    description: 'Sent 10,000 chat messages',
    icon: 'MessageSquare',
    rarity: 'epic',
    unlocked: false
  }, {
    id: 'badge-7',
    title: 'Media Maven',
    description: 'Collected 100 premium media items',
    icon: 'Image',
    rarity: 'rare',
    unlocked: true
  }, {
    id: 'badge-8',
    title: 'Streak Keeper',
    description: 'Maintained a 30-day login streak',
    icon: 'Zap',
    rarity: 'epic',
    unlocked: true
  }];
  const milestones: Milestone[] = [{
    id: 'milestone-1',
    title: 'Lover',
    level: 3,
    maxLevel: 5,
    progress: 1250,
    total: 2000,
    category: 'lover'
  }, {
    id: 'milestone-2',
    title: 'Messenger',
    level: 4,
    maxLevel: 5,
    progress: 850,
    total: 1000,
    category: 'messenger'
  }, {
    id: 'milestone-3',
    title: 'Collector',
    level: 2,
    maxLevel: 5,
    progress: 12,
    total: 25,
    category: 'collector'
  }, {
    id: 'milestone-4',
    title: 'Supporter',
    level: 5,
    maxLevel: 5,
    progress: 5000,
    total: 5000,
    category: 'supporter'
  }];
  const getIconComponent = (iconName: string, size: number = 24) => {
    switch (iconName) {
      case 'Heart':
        return <Heart size={size} data-id="element-23266" />;
      case 'Trophy':
        return <Trophy size={size} data-id="element-23267" />;
      case 'Star':
        return <Star size={size} data-id="element-23268" />;
      case 'Award':
        return <Award size={size} data-id="element-23269" />;
      case 'Shield':
        return <Shield size={size} data-id="element-23270" />;
      case 'MessageSquare':
        return <MessageSquare size={size} data-id="element-23271" />;
      case 'Image':
        return <Image size={size} data-id="element-23272" />;
      case 'Gift':
        return <Gift size={size} data-id="element-23273" />;
      case 'Zap':
        return <Zap size={size} data-id="element-23274" />;
      default:
        return <Trophy size={size} data-id="element-23275" />;
    }
  };
  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return 'from-gray-400 to-gray-500';
      case 'rare':
        return 'from-blue-400 to-blue-500';
      case 'epic':
        return 'from-purple-400 to-purple-500';
      case 'legendary':
        return 'from-amber-400 to-amber-500';
      default:
        return 'from-gray-400 to-gray-500';
    }
  };
  const getRarityBorderColor = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return 'border-gray-300 dark:border-gray-600';
      case 'rare':
        return 'border-blue-300 dark:border-blue-600';
      case 'epic':
        return 'border-purple-300 dark:border-purple-600';
      case 'legendary':
        return 'border-amber-300 dark:border-amber-600';
      default:
        return 'border-gray-300 dark:border-gray-600';
    }
  };
  const getRarityGlowColor = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return 'shadow-[0_0_8px_rgba(156,163,175,0.4)] dark:shadow-[0_0_8px_rgba(156,163,175,0.2)]';
      case 'rare':
        return 'shadow-[0_0_8px_rgba(59,130,246,0.4)] dark:shadow-[0_0_8px_rgba(59,130,246,0.2)]';
      case 'epic':
        return 'shadow-[0_0_8px_rgba(139,92,246,0.4)] dark:shadow-[0_0_8px_rgba(139,92,246,0.2)]';
      case 'legendary':
        return 'shadow-[0_0_8px_rgba(245,158,11,0.4)] dark:shadow-[0_0_8px_rgba(245,158,11,0.2)]';
      default:
        return 'shadow-[0_0_8px_rgba(156,163,175,0.4)] dark:shadow-[0_0_8px_rgba(156,163,175,0.2)]';
    }
  };
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'lover':
        return 'from-pink-500 to-purple-500';
      case 'messenger':
        return 'from-blue-500 to-cyan-500';
      case 'collector':
        return 'from-amber-500 to-orange-500';
      case 'supporter':
        return 'from-green-500 to-emerald-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };
  const navigateToAchievements = () => {
    // Navigate to the full achievements page
    const event = new CustomEvent('navigate', {
      detail: {
        section: 'achievements'
      }
    });
    document.dispatchEvent(event);
  };
  return <div className="space-y-6" data-id="element-23276">
      <Section title="Badges" data-id="element-23277">
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3" data-id="element-23278">
          {badges.map(badge => <div key={badge.id} className={`relative group ${!badge.unlocked ? 'opacity-50' : ''}`} data-id="element-23279">
              <div className={`w-full aspect-square rounded-lg flex items-center justify-center bg-white/80 dark:bg-gray-800/80 border-2 ${getRarityBorderColor(badge.rarity)} ${badge.unlocked ? getRarityGlowColor(badge.rarity) : ''} backdrop-blur-sm transition-all duration-300 group-hover:scale-105 overflow-hidden`} data-id="element-23280">
                {/* Animated background pattern */}
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-10 dark:opacity-20" data-id="element-23281"></div>
                {/* Gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${getRarityColor(badge.rarity)} opacity-10 dark:opacity-20`} data-id="element-23282"></div>
                {/* Energy streaks for unlocked badges */}
                {badge.unlocked && <>
                    <div className="absolute top-0 left-1/2 w-[1px] h-full bg-gradient-to-b from-transparent via-white to-transparent opacity-30 animate-pulse-subtle" data-id="element-23283"></div>
                    <div className="absolute top-1/2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse-subtle" style={{
                animationDelay: '0.5s'
              }} data-id="element-23284"></div>
                  </>}
                <div className={`relative ${badge.unlocked ? 'text-' + badge.rarity : 'text-gray-400 dark:text-gray-600'}`} data-id="element-23285">
                  {getIconComponent(badge.icon)}
                </div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 backdrop-blur-sm rounded-lg" data-id="element-23286">
                <div className="text-center p-1" data-id="element-23287">
                  <p className="text-white text-xs font-medium" data-id="element-23288">
                    {badge.title}
                  </p>
                  <p className={`text-[10px] ${badge.rarity === 'common' ? 'text-gray-300' : badge.rarity === 'rare' ? 'text-blue-300' : badge.rarity === 'epic' ? 'text-purple-300' : 'text-amber-300'}`} data-id="element-23289">
                    {badge.rarity.toUpperCase()}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </Section>
      <Section title="Milestones" data-id="element-23290">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-23291">
          {milestones.map(milestone => {
          const progressPercentage = milestone.progress / milestone.total * 100;
          return <div key={milestone.id} className="relative bg-white/80 dark:bg-gray-800/80 rounded-lg p-3 shadow-sm border border-gray-100/80 dark:border-gray-700/80 backdrop-blur-sm hover:shadow-md transition-all duration-300 overflow-hidden group" data-id="element-23292">
                {/* Background pattern */}
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/subtle-white-feathers.png')] opacity-5 dark:opacity-10" data-id="element-23293"></div>
                <div className="flex items-center gap-3 relative z-10" data-id="element-23294">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br ${getCategoryColor(milestone.category)} text-white shadow-[0_0_10px_rgba(168,85,247,0.3)] relative overflow-hidden`} data-id="element-23295">
                    {/* Animated energy for the icon */}
                    <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-all duration-700 transform rotate-0 group-hover:rotate-180" data-id="element-23296"></div>
                    {milestone.category === 'lover' && <Heart size={20} data-id="element-23297" />}
                    {milestone.category === 'messenger' && <MessageSquare size={20} data-id="element-23298" />}
                    {milestone.category === 'collector' && <Image size={20} data-id="element-23299" />}
                    {milestone.category === 'supporter' && <Gift size={20} data-id="element-23300" />}
                  </div>
                  <div className="flex-1" data-id="element-23301">
                    <div className="flex items-center justify-between" data-id="element-23302">
                      <h3 className="font-medium text-sm" data-id="element-23303">
                        {milestone.title}{' '}
                        <span className="text-xs" data-id="element-23304">Lv.{milestone.level}</span>
                      </h3>
                      <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-23305">
                        {milestone.level === milestone.maxLevel ? <span className="text-green-600 dark:text-green-400 font-semibold" data-id="element-23306">
                            MAX
                          </span> : `${Math.round(progressPercentage)}%`}
                      </span>
                    </div>
                    {milestone.level < milestone.maxLevel && <div className="mt-1.5" data-id="element-23307">
                        {/* Energy channel progress bar */}
                        <div className="relative h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-23308">
                          {/* Glowing progress indicator */}
                          <div className={`h-full rounded-full transition-all duration-500 bg-gradient-to-r ${getCategoryColor(milestone.category)} shadow-[0_0_8px_rgba(168,85,247,0.3)]`} style={{
                      width: `${progressPercentage}%`
                    }} data-id="element-23309"></div>
                          {/* Animated energy particles */}
                          <div className="absolute top-0 left-0 h-full w-full pointer-events-none" style={{
                      width: `${progressPercentage}%`,
                      overflow: 'hidden'
                    }} data-id="element-23310">
                            <div className="absolute top-0 right-0 h-full w-[5px] bg-white/50 blur-[2px] animate-pulse-subtle" data-id="element-23311"></div>
                            <div className="absolute top-0 right-[5px] h-full w-[2px] bg-white/30 blur-[1px] animate-pulse-subtle" style={{
                        animationDelay: '0.5s'
                      }} data-id="element-23312"></div>
                          </div>
                        </div>
                        <div className="flex justify-between mt-1 text-xs text-gray-600 dark:text-gray-400" data-id="element-23313">
                          <span data-id="element-23314">{milestone.progress}</span>
                          <span data-id="element-23315">{milestone.total}</span>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>;
        })}
        </div>
      </Section>
      <div className="flex justify-center" data-id="element-23316">
        <button className="relative px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-sm font-medium hover:shadow-[0_0_15px_rgba(245,158,11,0.5)] transition-all flex items-center gap-1.5 overflow-hidden group" onClick={navigateToAchievements} data-id="element-23317">
          {/* Button hover effect */}
          <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-23318"></div>
          <Trophy size={16} data-id="element-23319" />
          <span className="relative z-10" data-id="element-23320">See Full Achievements</span>
          <ChevronRight size={16} data-id="element-23321" />
        </button>
      </div>
    </div>;
};